import React, { createContext, useState, useContext } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    nav: {
      services: 'Services',
      about: 'About',
      contact: 'Contact'
    },
    hero: {
      tagline: 'DJ, Producer & Audio Engineer',
      cta: 'Get in Touch'
    },
    services: {
      title: 'Services',
      subtitle: 'Professional audio services to elevate your music productions to the next level',
      mixing: {
        title: 'Mixing',
        description: 'Professional mixing service for your music productions. Perfect balance between all elements, creating clarity, depth and sonic impact.'
      },
      mastering: {
        title: 'Mastering',
        description: 'Professional finalization of your tracks with industry standards. Loudness optimization, equalization and preparation for all streaming platforms.'
      },
      coproduction: {
        title: 'Co-production',
        description: 'Creative collaboration in developing your musical ideas. From initial concept to final production, working together to achieve your artistic vision.'
      }
    },
    about: {
      title: 'About',
      bio: [
        "Born in Brusque, Brazil, Ricardo Hingst, artistically known as HNGT, quickly emerges as one of the most exciting prospects in electronic music in the country. With a dynamic combination of talent and passion, he has not only solidified his name as a DJ and producer but also established himself as a prominent figure in the music scene.",
        "His journey into electronic music began after a transformative visit to the legendary Warung Beach Club in 2018, a moment that sparked an overwhelming passion for electronic music. He then dove into music production studies in 2020, starting a series of powerful releases and deepening his craft. Since then, HNGT has captivated audiences in renowned venues such as D EDGE, Elfortin, Terraza, and Surreal, in addition to reaching over 3 million streams on platforms, demonstrating the reach and impact of his music.",
        "With a style that blends Techno and Psytrance, HNGT stands out for creating an immersive atmosphere, with sounds that evoke space and the universe. His productions and live performances are designed to take the audience on a sonic journey that combines pulsating energy and cosmic textures, creating a deep connection between the audience and the environment.",
        "His performances in countries like Portugal, Germany, the Netherlands, Ireland, Romania, Costa Rica and Lebanon show the global acceptance of his music, impressing international audiences in various renowned clubs.",
        "The enthusiastic support of prominent figures like Amelie Lens, Victor Ruiz, Enrico Sangiuliano, Wehbba, Hilo, among others, is a testament to his exceptional skill and the powerful impact of his music worldwide.",
        "With a focus on continuing to explore new sounds and expand his collaborations, Ricardo Hingst (HNGT) remains a promising force in the electronic music industry, always pushing boundaries and leaving his indelible mark on the international scene."
      ]
    },
    contact: {
      title: 'Contact',
      subtitle: 'Ready to work together? Get in touch and let\'s create something incredible.',
      form: {
        name: 'Your name',
        email: 'Your email',
        message: 'Your message',
        submit: 'Send Message',
        sending: 'Sending...'
      },
      success: 'Success!',
      successMessage: 'Message sent successfully!',
      error: 'Error',
      errorMessage: 'An error occurred while sending the message. Please try again.'
    },
    footer: {
      copyright: 'All rights reserved.'
    }
  },
  pt: {
    nav: {
      services: 'Serviços',
      about: 'Sobre',
      contact: 'Contato'
    },
    hero: {
      tagline: 'DJ, Produtor & Engenheiro de Áudio',
      cta: 'Entre em Contato'
    },
    services: {
      title: 'Serviços',
      subtitle: 'Serviços profissionais de áudio para elevar suas produções musicais ao próximo nível',
      mixing: {
        title: 'Mixagem',
        description: 'Serviço profissional de mixagem para suas produções musicais. Equilíbrio perfeito entre todos os elementos, criando clareza, profundidade e impacto sonoro.'
      },
      mastering: {
        title: 'Masterização',
        description: 'Finalização profissional de suas faixas com padrões da indústria. Otimização de loudness, equalização e preparação para todas as plataformas de streaming.'
      },
      coproduction: {
        title: 'Co-produção',
        description: 'Colaboração criativa no desenvolvimento de suas ideias musicais. Desde o conceito inicial até a produção final, trabalhando juntos para alcançar sua visão artística.'
      }
    },
    about: {
      title: 'Sobre',
      bio: [
        "Nascido em Brusque, Brasil, Ricardo Hingst, artisticamente conhecido como HNGT, surge rapidamente como uma das promessas mais empolgantes da música eletrônica do país. Com uma combinação dinâmica de talento e paixão, ele não apenas solidificou seu nome como DJ e produtor, mas também se estabeleceu como uma figura proeminente na cena musical.",
        "Sua jornada na música eletrônica começou após uma visita transformadora ao lendário Warung Beach Club em 2018, um momento que despertou uma paixão avassaladora pela música eletrônica. Ele então mergulhou nos estudos de produção musical em 2020, iniciando uma série de lançamentos poderosos e aprofundando seu ofício. Desde então, HNGT cativou o público em locais renomados como D EDGE, Elfortin, Terraza e Surreal, além de atingir mais de 3 milhões de streams em plataformas, demonstrando o alcance e impacto de sua música.",
        "Com um estilo que mistura Techno e Psytrance, HNGT se destaca por criar uma atmosfera imersiva, com sons que evocam o espaço e o universo. Suas produções e performances ao vivo são projetadas para levar o público em uma jornada sonora que combina energia pulsante e texturas cósmicas, criando uma conexão profunda entre o público e o ambiente.",
        "Suas performances em países como Portugal, Alemanha, Holanda, Irlanda, Romênia, Costa Rica e Líbano mostram a aceitação global de sua música, impressionando públicos internacionais em diversos clubes renomados.",
        "O apoio entusiástico de figuras proeminentes como Amelie Lens, Victor Ruiz, Enrico Sangiuliano, Wehbba, Hilo, entre outros, é um testemunho de sua habilidade excepcional e do impacto poderoso de sua música em todo o mundo.",
        "Com foco em continuar explorando novos sons e expandir suas colaborações, Ricardo Hingst (HNGT) permanece uma força promissora na indústria da música eletrônica, sempre empurrando limites e deixando sua marca indelével na cena internacional."
      ]
    },
    contact: {
      title: 'Contato',
      subtitle: 'Pronto para trabalhar juntos? Entre em contato e vamos criar algo incrível.',
      form: {
        name: 'Seu nome',
        email: 'Seu email',
        message: 'Sua mensagem',
        submit: 'Enviar Mensagem',
        sending: 'Enviando...'
      },
      success: 'Sucesso!',
      successMessage: 'Mensagem enviada com sucesso!',
      error: 'Erro',
      errorMessage: 'Ocorreu um erro ao enviar a mensagem. Tente novamente.'
    },
    footer: {
      copyright: 'Todos os direitos reservados.'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('pt');

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'pt' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};